import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ImageOff, RefreshCw, Wrench, X, Square, ExternalLink } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { photoFull } from "@/lib/photoUrl";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

const FIELD_LABEL = {
  photo_url: "badImg.fieldPhoto",
  factory_quote_photo_url: "badImg.fieldFactoryQuote",
  pattern_photo_url: "badImg.fieldPattern",
  composite_photo_url: "badImg.fieldComposite",
};

export default function BadImages() {
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState("");
  const [counts, setCounts] = useState({});
  const timer = useRef(null);
  const pageSize = 50;

  const fetchList = useCallback(async (p = 1) => {
    try {
      const { data } = await api.get("/bad-images", { params: { page: p, page_size: pageSize } });
      setRows(data.items);
      setTotal(data.total);
      setCounts(data.counts || {});
      setPage(p);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/bad-images/scan/status");
      setStatus(data);
      if (data.state !== "running") {
        if (timer.current) { clearInterval(timer.current); timer.current = null; }
        fetchList(1);
      }
    } catch { /* not admin */ }
  }, [fetchList]);

  useEffect(() => {
    fetchStatus();
    fetchList(1);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [fetchStatus, fetchList]);

  useEffect(() => {
    if (status?.state === "running" && !timer.current) {
      timer.current = setInterval(fetchStatus, 3000);
    }
  }, [status, fetchStatus]);

  const startScan = async () => {
    setBusy("scan");
    try {
      await api.post("/bad-images/scan");
      toast.success(t("badImg.scanStarted"));
      fetchStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const stopScan = async () => {
    try {
      await api.post("/bad-images/scan/stop");
      toast.success(t("badImg.stopRequested"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const fixOne = async (row) => {
    setBusy(row.id);
    try {
      const { data } = await api.post(`/bad-images/${row.id}/fix`);
      if (data.fixed) {
        toast.success(t("badImg.fixed"));
        setRows((rs) => rs.filter((r) => r.id !== row.id));
        setTotal((n) => Math.max(0, n - 1));
      } else {
        toast.error(data.detail || t("badImg.fixFailed"));
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const dismiss = async (row) => {
    try {
      await api.delete(`/bad-images/${row.id}`);
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      setTotal((n) => Math.max(0, n - 1));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const running = status?.state === "running";
  const pct = status?.total ? Math.round((status.processed / status.total) * 100) : 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="bad-images-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-base font-bold tracking-tight flex items-center gap-2">
            <ImageOff size={18} className="text-brand" /> {t("badImg.title")}
          </h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">{t("badImg.hint")}</p>
        </div>
        <div className="flex items-center gap-2">
          {running && (
            <Button variant="outline" className="rounded-md" onClick={stopScan} data-testid="bad-img-stop">
              <Square size={13} /> {t("badImg.stop")}
            </Button>
          )}
          <Button variant="outline" className="rounded-md" onClick={startScan}
            disabled={running || busy === "scan"} data-testid="bad-img-scan">
            <RefreshCw size={14} className={running ? "animate-spin" : ""} />
            {running ? t("badImg.scanning") : t("badImg.scan")}
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md p-4 space-y-2" data-testid="bad-img-status">
        {running && (
          <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-brand transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        )}
        <p className="text-xs font-mono text-muted-foreground">
          {running
            ? `${t("badImg.scanning")} ${status.processed}/${status.total} (${pct}%) · ${t("badImg.checked")} ${status.checked} · ${t("badImg.found")} ${status.bad}`
            : status?.state === "idle"
              ? t("badImg.never")
              : `${t("badImg.lastRun")} ${status?.finished_at ? new Date(status.finished_at).toLocaleString() : "-"} · ${t("badImg.checked")} ${status?.checked ?? 0} · ${t("badImg.found")} ${status?.bad ?? 0}${status?.error ? ` · ${status.error}` : ""}`}
        </p>
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <Badge variant="secondary" data-testid="bad-img-total">{t("badImg.listCount").replace("{n}", total)}</Badge>
          {Object.entries(counts).map(([k, v]) => (
            <Badge key={k} variant="outline" className="font-mono text-[11px]">
              {t(`badImg.reason_${k}`) || k}: {v}
            </Badge>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        <Table data-testid="bad-images-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">{t("badImg.colPreview")}</TableHead>
              <TableHead>{t("products.itemNumber")}</TableHead>
              <TableHead>{t("products.description")}</TableHead>
              <TableHead>{t("badImg.colField")}</TableHead>
              <TableHead>{t("badImg.colReason")}</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8" data-testid="bad-img-empty">
                {t("badImg.empty")}
              </TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`bad-img-row-${r.id}`}>
                <TableCell>
                  <div className="w-16 h-16 border border-dashed border-border rounded flex items-center justify-center bg-secondary/40">
                    <img src={photoFull(r.url)} alt="" className="max-w-full max-h-full object-contain"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs font-semibold">
                  <Link to={`/products/${encodeURIComponent(r.item_number)}/edit`}
                    className="text-brand hover:underline inline-flex items-center gap-1"
                    data-testid={`bad-img-open-${r.id}`}>
                    {r.item_number} <ExternalLink size={11} />
                  </Link>
                </TableCell>
                <TableCell className="text-xs max-w-[320px] truncate">{r.description}</TableCell>
                <TableCell className="text-xs">{t(FIELD_LABEL[r.field]) || r.field}</TableCell>
                <TableCell>
                  <Badge variant={r.reason === "unsupported_format" ? "default" : "secondary"}
                    className="font-mono text-[10px]" data-testid={`bad-img-reason-${r.id}`}>
                    {t(`badImg.reason_${r.reason}`) || r.reason}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" title={t("badImg.fix")}
                      disabled={busy === r.id || r.field !== "photo_url"}
                      onClick={() => fixOne(r)} data-testid={`bad-img-fix-${r.id}`}>
                      <Wrench size={14} className={busy === r.id ? "animate-pulse" : ""} />
                    </Button>
                    <Button variant="ghost" size="icon" title={t("badImg.dismiss")}
                      onClick={() => dismiss(r)} data-testid={`bad-img-dismiss-${r.id}`}>
                      <X size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" className="rounded-md" disabled={page <= 1}
            onClick={() => fetchList(page - 1)} data-testid="bad-img-prev">{t("common.prev")}</Button>
          <span className="text-xs font-mono text-muted-foreground" data-testid="bad-img-page">{page} / {pages}</span>
          <Button variant="outline" size="sm" className="rounded-md" disabled={page >= pages}
            onClick={() => fetchList(page + 1)} data-testid="bad-img-next">{t("common.next")}</Button>
        </div>
      )}
    </div>
  );
}
